import { useCallback, useRef } from 'react'

import { AppId } from '@inno-tools/container-bridge'
import { useWindowManager } from 'modules/desktop/pages/MainPage/components/WindowManager/hooks/useWindowManager'
import { getLivechatContext } from 'shared/contexts/LiveChatSDKBridge'
import {
  getLiveChatAppUrl,
  getLiveChatUrl,
  getTopicIdFromAuthId,
  waitForLiveChatSession
} from 'shared/utils/liveChat'

/**
 * Opens a contact's conversation through the container's app window manager.
 *
 * Reads the SDK off the window bridge rather than `useLiveChatSDK()`: the top
 * dock also renders on routes that sit outside `BridgedLiveChatSDKProvider`,
 * where the hook would throw. When no conversation can be resolved — an
 * unreachable contact, or a page with no SDK at all — the Live Chat app is
 * opened anyway so the button is never dead.
 */
export const useOpenContactChat = () => {
  const { openAppWindow } = useWindowManager()
  const urlCache = useRef(new Map<number, string>())

  return useCallback(
    async (authId: number): Promise<void> => {
      const cachedUrl = urlCache.current.get(authId)
      if (cachedUrl) {
        openAppWindow(AppId.LIVE_CHAT, cachedUrl)
        return
      }

      const livechat = getLivechatContext()?.livechat

      openAppWindow(AppId.LIVE_CHAT, await getLiveChatAppUrl(''))
      await waitForLiveChatSession(livechat)
      const topicId = await getTopicIdFromAuthId(livechat, authId)
      if (!topicId) return
      const url = await getLiveChatUrl(topicId, '')
      if (!url) return

      // Only a real conversation is worth caching — let a miss retry next click.
      if (topicId) urlCache.current.set(authId, url)

      openAppWindow(AppId.LIVE_CHAT, url)
    },
    [openAppWindow]
  )
}
